import React, { useState, useEffect } from "react";
import { Trash2, AlertTriangle, X } from "lucide-react";
import { deleteAssignment } from "../../utils/assignment";
import { toast } from "react-toastify";
import { useT } from "../../stores/uiLang";

// Run E3 B4: this modal follows the UI language (EN text unchanged).
const CDA_T = {
  en: {
    failed: "Failed to delete assignment",
    title: "Delete Assignment",
    sure: "Are you sure you want to delete this assignment? This action cannot be undone.",
    warning: "Warning",
    warnBody: "All student progress and submissions for this assignment will be permanently removed.",
    cancel: "Cancel",
    deleting: "Deleting...",
    del: "Delete",
    typeChapter: "Chapter",
  },
  es: {
    failed: "No se pudo eliminar la tarea",
    title: "Eliminar la tarea",
    sure: "¿Seguro que quieres eliminar esta tarea? No se puede deshacer.",
    warning: "Aviso",
    warnBody: "Se borrará para siempre todo el avance y todas las entregas de los estudiantes en esta tarea.",
    cancel: "Cancelar",
    deleting: "Eliminando...",
    del: "Eliminar",
    typeChapter: "Capítulo",
  },
};

interface ConfirmDeleteAssignmentModalProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  assignment: {
    id: string;
    assessment_name: string;
    type: string;
    standard: string;
  } | null;
  onAssignmentDeleted: () => void;
}

const ConfirmDeleteAssignmentModal: React.FC<
  ConfirmDeleteAssignmentModalProps
> = ({ userId, isOpen, onClose, assignment, onAssignmentDeleted }) => {
  const t = useT(CDA_T);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!assignment) return;

    setIsDeleting(true);
    try {
      const result = await deleteAssignment(assignment.id, userId);

      if (result.success) {
        onAssignmentDeleted();
        onClose();
      } else {
        toast.error(result.error || t.failed);
      }
    } catch (error) {
      console.error("Error deleting assignment:", error);
      toast.error(t.failed);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !assignment) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal */}
        <div
          className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">
              {t.sure}
            </p>

            {/* Assignment Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <h4 className="text-base font-medium text-gray-900 mb-2">
                {assignment.assessment_name}
              </h4>
              <div className="flex items-center space-x-2 text-xs text-gray-600">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                  {assignment.type === "pe_chapter" ? t.typeChapter : assignment.type}
                </span>
                <span className="text-gray-500">{assignment.standard}</span>
              </div>
            </div>

            {/* Warning Message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-xs text-amber-800">
                  <p className="font-medium mb-0.5">{t.warning}</p>
                  <p>{t.warnBody}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    {t.deleting}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t.del}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmDeleteAssignmentModal;
